/**
 * EXP-1.4 · One flag, not two.
 *
 * Two columns used to mean "this field takes photos":
 *
 *   allow_attachments  written by the camera switch on the project field
 *                      (checklistGroupsService.updateItemAttachmentsSetting),
 *                      read by ProjectManagementTab to populate `photosEnabled`.
 *
 *   allow_photos       targeted by the template editor's "Allow photo
 *                      attachments" checkbox, and READ BY NOTHING.
 *
 * ⚠️ The planning note assumed the checkbox at least wrote its column. It did
 * not. `itemToAdd` and `itemToUpdate` in ChecklistItemManager both omitted
 * every photo flag, so the ticked value was dropped before the insert and the
 * admin's choice never left component state. `allow_photos` was therefore dead
 * on both sides, not a rival.
 *
 * This module is the single answer to "which column, and what does it mean".
 * The editor writes through `photoFlagPayload`, the field reads through
 * `itemAcceptsPhotos`, and `checklistPhotoFlag.test.ts` asserts the round trip.
 * Naming the column in one place is the point: a third flag arriving beside
 * two that already disagreed is what this story exists to prevent.
 */

/** The one column. Anything else naming a photo flag is a bug. */
export const CHECKLIST_PHOTO_COLUMN = 'allow_attachments' as const;

export interface PhotoFlagRow {
  allow_attachments?: boolean | null;
}

/**
 * The field's read: does this checklist item show photo tiles?
 *
 * Items created before the column existed carry NULL, which is off.
 */
export function itemAcceptsPhotos(
  row: PhotoFlagRow | null | undefined,
): boolean {
  return row?.allow_attachments === true;
}

/**
 * The template editor's write. Spread into the insert or update payload so the
 * checkbox lands on the column the field reads.
 */
export function photoFlagPayload(
  draft: PhotoFlagRow | null | undefined,
): { allow_attachments: boolean } {
  return { allow_attachments: itemAcceptsPhotos(draft) };
}
